import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Container,
  Group,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  Title,
  Modal,
  Tooltip,
  Loader,
  Flex,
  Pagination,
  Checkbox,
  MultiSelect,
  Switch
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { IconAlertTriangle, IconPlus, IconSearch, IconTrash, IconEye } from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import BreadcrumbsComponent from 'components/BreadcrumbsComponent';
import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { pathnames } from 'routes/dacum';
import { coreCompetencyService } from 'services/dacum/coreCompetency.service';
import { curriculumMappingService } from 'services/dacum/curriculumMapping.service';
import { courseService } from 'services/lcms/course.service';
import { showErrorNotification, showSuccessNotification } from 'utils/notification';

interface SelectedCourse {
  courseId: string;
  courseName: string;
  courseSequence: number;
  courseCode?: string;
  courseCategory?: string;
  status?: string;
  competencyCategory?: string;
  scheduleStartDate?: string | null;
  scheduleEndDate?: string | null;
  createdAt?: string;
  createdBy?: string;
  checkInByName?: string;
  checkInDateTime?: string | null;
  checkOutByName?: string;
  checkOutDateTime?: string | null;
  isRequired: number;
  positions: string[];
  learningObjectives?: string;
  learningHours?: number;
  modeOfDelivery?: string;
  businessUnits?: string[];
  liaIndicator?: boolean;
}

type CourseOption = {
  msLCMSCourseID: string;
  courseName: string;
  courseCode?: string;
  courseCategory?: string;
  status?: string;
  competencyCategory?: string;
  scheduleStartDate?: string | null;
  scheduleEndDate?: string | null;
  createdAt?: string;
  createdBy?: string;
  checkInByName?: string;
  checkInDateTime?: string | null;
  checkOutByName?: string;
  checkOutDateTime?: string | null;
};

type CourseSelectionPayload = {
  courseId: string;
  courseName: string;
  courseCode?: string;
  courseCategory?: string;
  status?: string;
  competencyCategory?: string;
  scheduleStartDate?: string | null;
  scheduleEndDate?: string | null;
  createdAt?: string;
  createdBy?: string;
  checkInByName?: string;
  checkInDateTime?: string | null;
  checkOutByName?: string;
  checkOutDateTime?: string | null;
};

const formatDateTime = (value?: string | null) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }
  return parsed.toLocaleString();
};

const formatCheckInfo = (name?: string, date?: string | null) => {
  if (!name && !date) return '-';
  const dateLabel = formatDateTime(date);
  if (!name) return dateLabel;
  if (dateLabel === '-') return name;
  return `${name} • ${dateLabel}`;
};

const decodeHtml = (html: string) => {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
};

// Category descriptions mapping
const CATEGORY_DESCRIPTIONS = {
  'Fundamental Thinking Skill':
    'The ability to explain essential information and apply it accurately to perform tasks and solve routine problems.\n\n',
  'Higher Order Thinking':
    'The ability to analyze situations, make sound judgments, and create effective solutions in complex or unfamiliar contexts.\n\n'
};

const AddCoreCompetencyPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // URL Parameters
  const jobFamilyId = searchParams.get('jobfamilyid') || '';
  const managementLevel = searchParams.get('managementlevel') || '';
  const businessUnitId = searchParams.get('businessunitid') || '';
  const businessUnitName = searchParams.get('businessunitname') || '';
  const jobFamilyName = searchParams.get('jobfamilyname') || '';
  const competencyType = searchParams.get('competencytype') || 'Core Competency';
  const competencyId = searchParams.get('competencyId') || '';
  const lpdCategoryDescription = searchParams.get('categoryDescription') || '';
  const lpdLevelDescription = searchParams.get('levelDescription') || '';
  const lpdCompetencyDescription = searchParams.get('competencyDescription') || '';
  const msSchoolCompetencyManagementID = searchParams.get('msSchoolCompetencyManagementID');

  const isFromLpd = Boolean(competencyId);

  // JOB 1: Fetch competency data using competencyId from curriculum-mapping API
  const { data: competencyData, isLoading } = useQuery({
    queryKey: ['competencyDetail', competencyId, businessUnitId, jobFamilyId, managementLevel],
    queryFn: () =>
      curriculumMappingService.getCurriculumMapping({
        jobfamilyid: jobFamilyId,
        managementlevel: managementLevel,
        businessunitid: businessUnitId,
        pageNumber: 1,
        pageSize: 10,
        competencyId: competencyId,
        competencyType: competencyType
      }),
    enabled: Boolean(competencyId),
    staleTime: 5 * 60 * 1000
  });

  // Extract job positions from API response (deduplicated)
  const jobPositions: { id: string; name: string }[] = React.useMemo(() => {
    const competency = competencyData?.data?.[0];
    if (!competency?.job || !Array.isArray(competency.job)) return [];

    // Use Map to deduplicate by jobPositionId
    const positionsMap = new Map<string, { id: string; name: string }>();

    competency.job.forEach((job) => {
      if (job.jobPositionId && job.jobPositionName) {
        positionsMap.set(job.jobPositionId, {
          id: job.jobPositionId,
          name: job.jobPositionName
        });
      }
    });

    return Array.from(positionsMap.values());
  }, [competencyData]);

  const [courseModalOpened, { open: openCourseModal, close: closeCourseModal }] =
    useDisclosure(false);
  const [selectedCourses, setSelectedCourses] = React.useState<SelectedCourse[]>([]);
  const [loadingCourses, setLoadingCourses] = React.useState(false);
  const [coursesFetched, setCoursesFetched] = React.useState(false);
  const [competencyDescription, setCompetencyDescription] = React.useState<string>('');
  const [useManualInput, setUseManualInput] = React.useState(false);
  const [fetchingCourseDetails, setFetchingCourseDetails] = React.useState(false);

  const { data: jobProfilesData } = useQuery({
    queryKey: ['jobProfiles', businessUnitId, jobFamilyId, managementLevel],
    queryFn: async () => {
      const response = await courseService.getLCMSJobProfiles({
        bu: businessUnitId === 'All' ? '' : businessUnitId,
        jobFamilyId: jobFamilyId === 'All' ? '' : jobFamilyId,
        managementLevel: managementLevel === 'All' ? '' : managementLevel,
        pageNumber: 1,
        pageSize: 100,
        search: ''
      });
      return response;
    }
  });

  // Determine if we should show competency name dropdown
  const shouldShowCompetencyDropdown = React.useMemo(() => {
    const lowerType = competencyType.toLowerCase();
    return lowerType.includes('core') || lowerType.includes('leader') || lowerType.includes('fm');
  }, [competencyType]);

  // Determine the category for API call based on competency type
  const competencyCategory = React.useMemo(() => {
    const lowerType = competencyType.toLowerCase();
    if (lowerType.includes('leader')) return 'leader';
    if (lowerType.includes('core')) return 'core';
    if (lowerType.includes('fm')) return 'fm';
    return '';
  }, [competencyType]);

  // Query for fetching competency names
  const { data: competencyNamesData } = useQuery({
    queryKey: ['competencyNames', competencyCategory, managementLevel],
    queryFn: () =>
      coreCompetencyService.listCompetencyNames({
        category: competencyCategory,
        jobband: managementLevel,
        pageNumber: 1,
        pageSize: 100
      }),
    enabled:
      shouldShowCompetencyDropdown && Boolean(competencyCategory) && Boolean(managementLevel),
    staleTime: 5 * 60 * 1000
  });

  const form = useForm({
    initialValues: {
      jobPositions: [] as string[],
      competencyName: '',
      category: '',
      level: '1',
      categoryDescription: '',
      levelDescription: '',
      courses: [] as string[],
      schoolName: '', // Keep for payload but won't be validated
      tahun: new Date().getFullYear().toString(),
      note: ''
    },
    validate: {
      jobPositions: (value: string[]) =>
        value.length === 0 ? 'At least one job position is required' : null,
      competencyName: (value: string) => (!value ? 'Competency name is required' : null),
      // Category and Level are no longer required for Core/Leader
      tahun: (value: string) => {
        // Year is only required for non-Core/Leader/FM competencies
        const lowerType = competencyType.toLowerCase();
        const isCoreOrLeaderOrFM =
          lowerType.includes('core') || lowerType.includes('leader') || lowerType.includes('fm');
        if (!isCoreOrLeaderOrFM && !value) {
          return 'Year is required';
        }
        return null;
      }
    },
    validateInputOnBlur: true,
    validateInputOnChange: ['jobPositions', 'competencyName']
  });

  // Custom validation for courses (checked on submit)
  const validateCourses = () => {
    if (selectedCourses.length === 0) {
      showErrorNotification({ message: 'At least one course is required' });
      return false;
    }
    return true;
  };

  // Handle prefill from competency detail
  React.useEffect(() => {
    // If competencyId is provided, use fetched data
    if (competencyId && competencyData?.data && competencyData.data.length > 0) {
      const competency = competencyData.data[0];

      // Set job positions from the fetched data
      if (competency.job && Array.isArray(competency.job)) {
        const jobPositionIds = competency.job.map((pos: any) => pos.jobPositionId);
        form.setFieldValue('jobPositions', jobPositionIds);
      }

      // Set competency details
      if (competency.competencyName) {
        form.setFieldValue('competencyName', competency.competencyName);

        // If dropdown is enabled, try to find matching competency and set description
        if (shouldShowCompetencyDropdown && competencyNamesData?.data) {
          const matchingCompetency = competencyNamesData.data.find(
            (item) => item.competencyName === competency.competencyName
          );
          if (matchingCompetency) {
            setCompetencyDescription(matchingCompetency.competencyDescription);
          }
        }
      }
      if (competency.category) {
        form.setFieldValue('category', competency.category);
      }
      if (competency.level) {
        form.setFieldValue('level', competency.level.toString());
      }
      if (competency.categoryDescription) {
        // competency.categoryDescription = Category description → levelDescription form field
        form.setFieldValue('levelDescription', competency.categoryDescription);
      } else if (lpdLevelDescription) {
        form.setFieldValue('levelDescription', lpdLevelDescription);
      }
      // LPD 'description' field = Category Description → categoryDescription form field
      if (competency.description) {
        form.setFieldValue('categoryDescription', competency.description);
      } else if (lpdCategoryDescription) {
        // Fallback: use the categoryDescription passed directly from the LPD navigation
        form.setFieldValue('categoryDescription', lpdCategoryDescription);
      }
      if (competency.levelDescription) {
        // levelDescription from API (non-LPD sources) → levelDescription form field
        if (!competency.categoryDescription) {
          form.setFieldValue('levelDescription', competency.levelDescription);
        }
      }
      if (competency.note) {
        form.setFieldValue('note', competency.note);
      }

      // Pre-fill competency description from LPD navigation param
      if (lpdCompetencyDescription) {
        setCompetencyDescription(lpdCompetencyDescription);
      }
    }
  }, [
    competencyId,
    competencyData,
    competencyType,
    shouldShowCompetencyDropdown,
    competencyNamesData,
    lpdCategoryDescription,
    lpdLevelDescription,
    lpdCompetencyDescription
  ]); // Dependencies for fetched data

  React.useEffect(() => {
    if (!competencyId || !competencyData?.data || competencyData.data.length === 0) return;
    if (coursesFetched) return;

    const fetchCoursesFromCompetency = async () => {
      setLoadingCourses(true);

      const coursesMap = new Map<string, { idCourse: string; courseName: string }>();

      competencyData.data.forEach((comp) => {
        if (comp.idCourse && comp.courseName) {
          coursesMap.set(comp.idCourse, {
            idCourse: comp.idCourse,
            courseName: comp.courseName
          });
        }
      });

      const uniqueCourses = Array.from(coursesMap.values());

      if (uniqueCourses.length === 0) {
        setLoadingCourses(false);
        setCoursesFetched(true);
        return;
      }

      // Get current job position names from form
      const currentJobPositions = form.values.jobPositions.map((posId) => {
        const pos = jobPositions.find((p) => p.id === posId);
        if (pos) return pos.name;

        const jobProfile = jobProfilesData?.data?.find((pos) => pos.jobProfileId === posId);
        if (jobProfile) return jobProfile.jobProfileName;

        return posId;
      });

      // Fetch course details from LCMS
      const coursesPromises = uniqueCourses.map(async (course) => {
        try {
          // Try to fetch from LCMS using detailByLms endpoint
          const response = await courseService.detailByLms(course.idCourse);
          const detailData = response.data?.data;

          if (detailData && detailData.msLCMSCourseID) {
            return {
              courseId: detailData.msLCMSCourseID, // Use msLCMSCourseID from LCMS
              courseName: detailData.basicInformation?.courseName || course.courseName,
              courseSequence: 0,
              courseCode: detailData?.basicInformation?.courseCode || '',
              courseCategory: detailData?.basicInformation?.courseCategory || '',
              status: detailData?.status || '',
              competencyCategory: detailData?.basicInformation?.competencyCategory || '',
              scheduleStartDate: null,
              scheduleEndDate: null,
              createdAt: '',
              createdBy: '',
              checkInByName: '',
              checkInDateTime: null,
              checkOutByName: '',
              checkOutDateTime: null,
              isRequired: 1,
              positions: currentJobPositions,
              learningObjectives: detailData?.deliveryDetails?.learningObjectives,
              learningHours: detailData?.deliveryDetails?.learningHours,
              modeOfDelivery: detailData?.basicInformation?.modeOfDelivery,
              businessUnits: detailData?.basicInformation?.businessUnits,
              liaIndicator: detailData?.indicatorStandards?.liaIndicator
            };
          } else {
            // Course not found in LCMS, return null to skip it
            return null;
          }
        } catch {
          // Error fetching course, return null to skip it
          return null;
        }
      });

      const coursesResults = await Promise.all(coursesPromises);

      // Filter out null values (courses not found in LCMS)
      const coursesWithDetails = coursesResults.filter(
        (course): course is NonNullable<typeof course> => course !== null
      );

      if (coursesWithDetails.length === 0) {
        setLoadingCourses(false);
        return;
      }

      // Add sequence numbers
      const coursesWithSequence = coursesWithDetails.map((course, index) => ({
        ...course,
        courseSequence: index + 1
      }));

      setSelectedCourses(coursesWithSequence);
      setLoadingCourses(false);
      setCoursesFetched(true);
    };

    fetchCoursesFromCompetency();
  }, [competencyId, competencyData, coursesFetched, jobPositions, jobProfilesData]);

  const createMutation = useMutation({
    mutationFn: coreCompetencyService.create,
    onSuccess: () => {
      showSuccessNotification({ message: 'Core competency added successfully' });
      // Navigate back to detail competency page with preserved search params
      const detailPath = `${pathnames.detailCompetencyManagement}?${searchParams.toString()}`;
      navigate(detailPath);
    },
    onError: (error: any) => {
      showErrorNotification({ message: error?.message || 'Failed to add competency' });
    }
  });

  const handleAddCourses = React.useCallback(
    async (courses: any[]) => {
      setFetchingCourseDetails(true);
      const existingCourseIds = new Set(selectedCourses.map((course) => course.courseId));
      const newCourses = courses.filter((course) => !existingCourseIds.has(course.courseId));

      // Get current job position names from form
      const currentJobPositions = form.values.jobPositions.map((posId) => {
        const pos = jobPositions.find((p) => p.id === posId);
        if (pos) return pos.name;

        const jobProfile = jobProfilesData?.data?.find((pos) => pos.jobProfileId === posId);
        if (jobProfile) return jobProfile.jobProfileName;

        return posId;
      });

      const coursesWithDetails = await Promise.all(
        newCourses.map(async (course) => {
          try {
            // Always use detail endpoint when manually adding courses
            const response = await courseService.detail(course.courseId);
            const detailData = response.data?.data;

            return {
              courseId: course.courseId,
              courseName: course.courseName,
              courseSequence: 0,
              courseCode: detailData?.basicInformation?.courseCode || '',
              courseCategory: detailData?.basicInformation?.courseCategory || '',
              status: detailData?.status || '',
              competencyCategory: detailData?.basicInformation?.competencyCategory || '',
              scheduleStartDate: null,
              scheduleEndDate: null,
              createdAt: '',
              createdBy: '',
              checkInByName: '',
              checkInDateTime: null,
              checkOutByName: '',
              checkOutDateTime: null,
              isRequired: 1,
              positions: currentJobPositions, // Auto-assign current job positions
              learningObjectives: detailData?.deliveryDetails?.learningObjectives,
              learningHours: detailData?.deliveryDetails?.learningHours,
              modeOfDelivery: detailData?.basicInformation?.modeOfDelivery,
              businessUnits: detailData?.basicInformation?.businessUnits,
              liaIndicator: detailData?.indicatorStandards?.liaIndicator
            };
          } catch {
            return {
              courseId: course.courseId,
              courseName: course.courseName,
              courseSequence: 0,
              courseCode: '',
              courseCategory: '',
              status: '',
              competencyCategory: '',
              scheduleStartDate: null,
              scheduleEndDate: null,
              createdAt: '',
              createdBy: '',
              checkInByName: '',
              checkInDateTime: null,
              checkOutByName: '',
              checkOutDateTime: null,
              isRequired: 1,
              positions: currentJobPositions, // Auto-assign current job positions
              learningObjectives: undefined,
              learningHours: undefined,
              modeOfDelivery: undefined,
              businessUnits: undefined,
              liaIndicator: undefined
            };
          }
        })
      );

      setSelectedCourses((prev) => {
        const existingIds = new Set(prev.map((item) => item.courseId));
        const nextCourses = [
          ...prev,
          ...coursesWithDetails
            .filter((course) => !existingIds.has(course.courseId))
            .map((course, index) => ({
              ...course,
              courseSequence: prev.length + index + 1
            }))
        ];
        return nextCourses;
      });
      setFetchingCourseDetails(false);
      closeCourseModal();
    },
    [selectedCourses, closeCourseModal, form.values.jobPositions, jobPositions, jobProfilesData]
  );

  // Auto-sync positions when job positions change
  React.useEffect(() => {
    const currentJobPositions = form.values.jobPositions.map((posId) => {
      const pos = jobPositions.find((p) => p.id === posId);
      if (pos) return pos.name;

      const jobProfile = jobProfilesData?.data?.find((pos: any) => pos.jobProfileId === posId);
      if (jobProfile) return jobProfile.jobProfileName;

      return posId;
    });

    // Update all courses with the new positions
    setSelectedCourses((prev) =>
      prev.map((course) => ({
        ...course,
        positions: currentJobPositions
      }))
    );
  }, [form.values.jobPositions, jobPositions, jobProfilesData]);

  const handleSubmit = (values: typeof form.values) => {
    // Validate courses before submitting
    if (!validateCourses()) {
      return;
    }

    const jobPositionsList = values.jobPositions.map((posId) => {
      const pos = jobPositions.find((p) => p.id === posId);
      if (pos) {
        return { id: pos.id, name: pos.name };
      }

      // Check if it's from job profiles
      const jobProfile = jobProfilesData?.data?.find((pos: any) => pos.jobProfileId === posId);
      if (jobProfile) {
        return { id: jobProfile.jobProfileId, name: jobProfile.jobProfileName };
      }

      // Fallback: use the value as both id and name
      return { id: posId, name: posId };
    });

    const payload: any = {
      // For Technical Competency, send names instead of IDs
      businessUnitId: competencyType === 'Technical Competency' ? businessUnitName : businessUnitId,
      businessUnitName,
      jobFamilyId: competencyType === 'Technical Competency' ? jobFamilyName : jobFamilyId,
      jobFamilyName,
      managementLevel,
      competencyType,
      schoolName: '', // Always send empty schoolName
      jobPositions: jobPositionsList, // Send array of {id, name}
      competencyName: values.competencyName,
      competencyDescription,
      msSchoolCompetencyManagementID:
        competencyType === 'Technical Competency'
          ? 'D0ED6495-977F-4C62-9D4C-F8C73B9C7729'
          : msSchoolCompetencyManagementID || null,
      category: values.category,
      level: parseInt(values.level),
      categoryDescription: values.categoryDescription,
      levelDescription: values.levelDescription,
      note: values.note,
      year: parseInt(values.tahun),
      courses: selectedCourses.map((course) => ({
        courseId: course.courseId,
        courseName: course.courseName,
        positions: course.positions || []
      }))
    };

    if (competencyType === 'Technical Competency' && competencyId) {
      payload.idLPD = competencyId;
    }

    createMutation.mutate(payload);
  };

  const handleCancel = () => {
    // Clear localStorage when canceling
    localStorage.removeItem('dacum_prefill_job_positions');
    localStorage.removeItem('dacum_lpd_job_positions');
    navigate(-1);
  };

  const breadcrumbs = [
    { title: 'Competency Management', href: pathnames.competencyManagement },
    {
      title: 'Detail Competency',
      href: `${pathnames.detailCompetencyManagement}?${searchParams.toString()}`
    },
    { title: 'Add Competency', href: '' }
  ];

  return (
    <Container fluid>
      <BreadcrumbsComponent items={breadcrumbs} mb={32} />

      {/* Loading state when fetching from LPD */}
      {isFromLpd && isLoading && (
        <Paper p='xl' mb='md' withBorder>
          <Flex direction='column' align='center' gap='md' py='xl'>
            <Loader size='lg' type='dots' />
            <Stack gap='xs' align='center'>
              <Text size='lg' fw={600}>
                Loading Competency Data
              </Text>
              <Text size='sm' c='dimmed' ta='center' maw={400}>
                Fetching competency details from LPD to pre-fill the form...
              </Text>
            </Stack>
          </Flex>
        </Paper>
      )}

      <Paper p='xl'>
        <Stack gap='xl'>
          <div>
            <Title order={3}>Add Competency</Title>
            <Text size='sm' c='dimmed' mt='xs'>
              Create a new core competency for {businessUnitName} Business Unit - {jobFamilyName}{' '}
              Job Family with {managementLevel} Job Band.
            </Text>
            {isFromLpd && !isLoading && (
              <Text size='sm' c='blue' mt='xs' fw={500}>
                ✓ Pre-filled from LPD data
              </Text>
            )}
          </div>

          {!msSchoolCompetencyManagementID && (
            <Alert
              icon={<IconAlertTriangle size={18} />}
              title='School Competency Not Mapped'
              color='orange'
              variant='light'
            >
              This competency does not have a school competency mapped in the system. School
              competency mapping is <strong>required</strong> before this competency can be included
              in a master plan submission. Please ensure a school competency is mapped first.
            </Alert>
          )}

          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap='lg'>
              <MultiSelect
                label='Job Position'
                placeholder='Select job positions'
                data={(() => {
                  const positions =
                    jobPositions.length > 0
                      ? jobPositions.map((pos) => ({
                          value: pos.id,
                          label: pos.name
                        }))
                      : jobProfilesData?.data?.map((item) => ({
                          value: item.jobProfileId,
                          label: item.jobProfileName
                        })) || [];

                  const selectedCount = form.values.jobPositions.length;
                  const options = [];

                  if (selectedCount === 0) {
                    options.push({ value: '__SELECT_ALL__', label: '✓ Select All Job Positions' });
                  }

                  if (selectedCount > 4) {
                    options.push({
                      value: '__DESELECT_ALL__',
                      label: '✕ Deselect All Job Positions'
                    });
                  }

                  return [...options, ...positions];
                })()}
                value={form.values.jobPositions}
                onChange={(values) => {
                  // Check if "Select All" was clicked
                  if (values.includes('__SELECT_ALL__')) {
                    const allPositions =
                      jobPositions.length > 0
                        ? jobPositions.map((pos) => pos.id)
                        : jobProfilesData?.data?.map((item) => item.jobProfileId) || [];
                    form.setFieldValue('jobPositions', allPositions);
                  } else if (values.includes('__DESELECT_ALL__')) {
                    // Check if "Deselect All" was clicked
                    form.setFieldValue('jobPositions', []);
                  } else {
                    form.setFieldValue('jobPositions', values);
                  }
                }}
                searchable
                disabled={isFromLpd}
                required
                error={form.errors.jobPositions}
              />

              {shouldShowCompetencyDropdown ? (
                <Stack gap='xs'>
                  {!isFromLpd && (
                    <Switch
                      label='Enter competency name manually'
                      description='Toggle this to type a custom competency name instead of selecting from the list'
                      checked={useManualInput}
                      onChange={(event) => {
                        setUseManualInput(event.currentTarget.checked);
                        if (event.currentTarget.checked) {
                          // Clear description when switching to manual
                          setCompetencyDescription('');
                        }
                      }}
                    />
                  )}
                  {useManualInput ? (
                    <TextInput
                      label='Competency Name'
                      placeholder='Enter competency name'
                      {...form.getInputProps('competencyName')}
                      required
                    />
                  ) : (
                    <Select
                      label='Competency Name'
                      placeholder='Select competency name'
                      data={
                        competencyNamesData?.data?.map((item) => ({
                          value: item.competencyName,
                          label: item.competencyName
                        })) || []
                      }
                      {...form.getInputProps('competencyName')}
                      onChange={(value) => {
                        form.setFieldValue('competencyName', value || '');
                        // Find and set the description
                        const selectedCompetency = competencyNamesData?.data?.find(
                          (item) => item.competencyName === value
                        );
                        setCompetencyDescription(selectedCompetency?.competencyDescription || '');
                      }}
                      disabled={isFromLpd}
                      searchable
                      required
                    />
                  )}
                </Stack>
              ) : (
                <TextInput
                  label='Competency Name'
                  placeholder='Enter competency name'
                  {...form.getInputProps('competencyName')}
                  readOnly={isFromLpd}
                  styles={
                    isFromLpd
                      ? {
                          input: {
                            backgroundColor: 'var(--mantine-color-gray-0)',
                            cursor: 'not-allowed'
                          }
                        }
                      : undefined
                  }
                  required
                />
              )}

              <Textarea
                label='Competency Description'
                value={competencyDescription}
                onChange={(event) => setCompetencyDescription(event.currentTarget.value)}
                minRows={3}
                autosize
              />

              <Select
                label='Level'
                placeholder='Select level'
                data={[
                  { value: '1', label: '1' },
                  { value: '2', label: '2' },
                  { value: '3', label: '3' },
                  { value: '4', label: '4' },
                  { value: '5', label: '5' }
                ]}
                {...form.getInputProps('level')}
                disabled={isFromLpd}
              />

              <Textarea
                label='Level Description'
                placeholder='Enter level description'
                {...form.getInputProps('levelDescription')}
                minRows={3}
                autosize
              />

              {competencyType === 'Technical Competency' ? (
                <Textarea
                  label='Category Description'
                  placeholder='Enter category description'
                  value={form.values.categoryDescription}
                  onChange={(event) => {
                    const value = event.currentTarget.value;
                    form.setFieldValue('categoryDescription', value);
                    if (
                      value.toLowerCase().includes('high') ||
                      value.toLowerCase().includes('higher')
                    ) {
                      form.setFieldValue('category', 'Higher Order Thinking');
                    } else if (value.toLowerCase().includes('fundamental')) {
                      form.setFieldValue('category', 'Fundamental Thinking Skill');
                    } else {
                      // Default or keep existing
                      if (!form.values.category) {
                        form.setFieldValue('category', 'Fundamental Thinking Skill');
                      }
                    }
                  }}
                  minRows={3}
                  autosize
                  disabled={isFromLpd && Boolean(form.values.categoryDescription)}
                  styles={
                    isFromLpd && Boolean(form.values.categoryDescription)
                      ? {
                          input: {
                            backgroundColor: 'var(--mantine-color-gray-0)',
                            cursor: 'not-allowed'
                          }
                        }
                      : undefined
                  }
                />
              ) : (
                <>
                  <Select
                    label='Category'
                    placeholder='Select category'
                    data={[
                      { value: 'Higher Order Thinking', label: 'Higher Order Thinking' },
                      { value: 'Fundamental Thinking Skill', label: 'Fundamental Thinking Skill' }
                    ]}
                    value={form.values.category}
                    onChange={(value) => {
                      form.setFieldValue('category', value || '');

                      if (
                        value &&
                        CATEGORY_DESCRIPTIONS[value as keyof typeof CATEGORY_DESCRIPTIONS]
                      ) {
                        const currentDesc = form.values.categoryDescription;
                        const newPrefix =
                          CATEGORY_DESCRIPTIONS[value as keyof typeof CATEGORY_DESCRIPTIONS];

                        // Remove old prefix if exists
                        const oldPrefixes = Object.values(CATEGORY_DESCRIPTIONS);
                        let cleanDesc = currentDesc;
                        oldPrefixes.forEach((prefix) => {
                          if (cleanDesc.startsWith(prefix)) {
                            cleanDesc = cleanDesc.substring(prefix.length);
                          }
                        });

                        // Add new prefix
                        form.setFieldValue('categoryDescription', newPrefix + cleanDesc);
                      }
                    }}
                    error={form.errors.category}
                    disabled={isFromLpd && Boolean(form.values.category)}
                    styles={
                      isFromLpd && Boolean(form.values.category)
                        ? {
                            input: {
                              backgroundColor: 'var(--mantine-color-gray-0)',
                              cursor: 'not-allowed',
                              color: 'var(--mantine-color-black)'
                            }
                          }
                        : undefined
                    }
                  />

                  <Textarea
                    label='Category Description'
                    placeholder='Enter category description'
                    {...form.getInputProps('categoryDescription')}
                    minRows={3}
                    autosize
                  />
                </>
              )}

              {!shouldShowCompetencyDropdown && (
                <Select
                  label='Year'
                  placeholder='Select year'
                  data={(() => {
                    const currentYear = new Date().getFullYear();
                    const years = [];
                    for (let i = currentYear - 2; i <= currentYear + 5; i++) {
                      years.push({ value: i.toString(), label: i.toString() });
                    }
                    return years;
                  })()}
                  {...form.getInputProps('tahun')}
                  required
                />
              )}

              <Textarea
                label='Note'
                placeholder='Enter note'
                {...form.getInputProps('note')}
                minRows={3}
                autosize
              />

              <div>
                <Group justify='space-between' mb='md'>
                  <Text fw={600}>Selected Courses</Text>
                  {selectedCourses.length === 0 && !loadingCourses && (
                    <Button
                      leftSection={<IconPlus size={16} />}
                      onClick={openCourseModal}
                      variant='light'
                    >
                      Select Courses
                    </Button>
                  )}
                </Group>

                {isFromLpd && selectedCourses.length > 0 && (
                  <Paper p='sm' mb='md' withBorder bg='blue.0'>
                    <Text size='sm' c='blue' fw={500}>
                      ✓ {selectedCourses.length} course{selectedCourses.length > 1 ? 's' : ''}{' '}
                      auto-loaded from competency data
                    </Text>
                  </Paper>
                )}

                {loadingCourses ? (
                  <Paper p='md' withBorder>
                    <Flex direction='column' align='center' gap='md' py='xl'>
                      <Loader size='md' type='dots' />
                      <Stack gap='xs' align='center'>
                        <Text size='md' fw={600}>
                          Loading Courses from LCMS
                        </Text>
                        <Text size='sm' c='dimmed' ta='center' maw={400}>
                          Fetching course details and checking availability in LCMS...
                        </Text>
                      </Stack>
                    </Flex>
                  </Paper>
                ) : selectedCourses.length === 0 ? (
                  <Paper p='md' withBorder>
                    <Text size='sm' c='dimmed' ta='center'>
                      No courses selected yet. Click "Select Courses" to add courses.
                    </Text>
                  </Paper>
                ) : (
                  <ScrollArea>
                    <Table striped withTableBorder highlightOnHover>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th style={{ width: 60 }}>#</Table.Th>
                          <Table.Th>Course Name</Table.Th>
                          <Table.Th>Learning Objectives</Table.Th>
                          <Table.Th>Competency</Table.Th>
                          <Table.Th>Learning Hours</Table.Th>
                          <Table.Th>Mode of Delivery</Table.Th>
                          <Table.Th style={{ width: 80 }}>Actions</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {selectedCourses.map((row, index) => (
                          <Table.Tr key={row.courseId}>
                            <Table.Td>
                              <Text fw={600}>{index + 1}</Text>
                            </Table.Td>
                            <Table.Td>
                              <Stack gap={4} align='flex-start'>
                                <Group gap='xs'>
                                  <Anchor
                                    href={`/app/lcms/courses/${row.courseId}`}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    size='md'
                                    fw={600}
                                  >
                                    {row.courseName}
                                  </Anchor>
                                  {isFromLpd && (
                                    <Badge size='xs' color='green' variant='dot'>
                                      From LPD
                                    </Badge>
                                  )}
                                </Group>
                                <Text size='xs' c='dimmed'>
                                  ID: {row.courseId}
                                </Text>
                              </Stack>
                            </Table.Td>
                            <Table.Td style={{ maxWidth: 200 }}>
                              <Text size='sm' lineClamp={2}>
                                {row.learningObjectives ? decodeHtml(row.learningObjectives) : '-'}
                              </Text>
                            </Table.Td>
                            <Table.Td>{row.competencyCategory || '-'}</Table.Td>
                            <Table.Td>{row.learningHours || '-'}</Table.Td>
                            <Table.Td>{row.modeOfDelivery || '-'}</Table.Td>

                            <Table.Td>
                              <Group justify='flex-end' gap={4}>
                                <Tooltip label='View Course Details'>
                                  <ActionIcon
                                    variant='light'
                                    color='blue'
                                    size='sm'
                                    component='a'
                                    href={`/app/lcms/courses/${row.courseId}`}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                  >
                                    <IconEye size={16} />
                                  </ActionIcon>
                                </Tooltip>
                                <ActionIcon
                                  variant='light'
                                  color='red'
                                  size='sm'
                                  onClick={() =>
                                    setSelectedCourses((prev) =>
                                      prev.filter((c) => c.courseId !== row.courseId)
                                    )
                                  }
                                >
                                  <IconTrash size={16} />
                                </ActionIcon>
                              </Group>
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </ScrollArea>
                )}

                {selectedCourses.length > 0 && !loadingCourses && (
                  <Group justify='center' mt='md'>
                    <Button
                      leftSection={<IconPlus size={16} />}
                      onClick={openCourseModal}
                      variant='light'
                      size='sm'
                    >
                      Add More Courses
                    </Button>
                  </Group>
                )}
              </div>

              <Group justify='flex-end' mt='xl'>
                <Button variant='default' onClick={handleCancel}>
                  Cancel
                </Button>
                <Button type='submit' loading={createMutation.isPending}>
                  Add Competency
                </Button>
              </Group>
            </Stack>
          </form>
        </Stack>
      </Paper>

      <CourseSelectionModal
        opened={courseModalOpened}
        onClose={closeCourseModal}
        onSubmit={handleAddCourses}
        selectedCourses={selectedCourses}
        competencyType={competencyType}
        jobPositions={form.values.jobPositions.map((posId) => {
          const pos = jobPositions.find((p) => p.id === posId);
          if (pos) return pos.name;
          const jobProfile = jobProfilesData?.data?.find((pos) => pos.jobProfileId === posId);
          if (jobProfile) return jobProfile.jobProfileName;
          return posId;
        })}
        fetchingCourseDetails={fetchingCourseDetails}
      />
    </Container>
  );
};

interface CourseSelectionModalProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (_courses: CourseSelectionPayload[]) => void;
  selectedCourses: SelectedCourse[];
  competencyType: string;
  jobPositions: string[];
  fetchingCourseDetails: boolean;
}

const CourseSelectionModal: React.FC<CourseSelectionModalProps> = ({
  opened,
  onClose,
  onSubmit,
  selectedCourses,
  competencyType,
  jobPositions,
  fetchingCourseDetails
}) => {
  const [searchKeyword, setSearchKeyword] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [selection, setSelection] = React.useState<Record<string, CourseOption>>({});

  React.useEffect(() => {
    if (opened) {
      const initialSelection: Record<string, CourseOption> = {};
      selectedCourses.forEach((course) => {
        initialSelection[course.courseId] = {
          msLCMSCourseID: course.courseId,
          courseName: course.courseName,
          courseCode: course.courseCode,
          courseCategory: course.courseCategory,
          status: course.status,
          competencyCategory: course.competencyCategory,
          scheduleStartDate: course.scheduleStartDate,
          scheduleEndDate: course.scheduleEndDate,
          createdAt: course.createdAt,
          createdBy: course.createdBy,
          checkInByName: course.checkInByName,
          checkInDateTime: course.checkInDateTime,
          checkOutByName: course.checkOutByName,
          checkOutDateTime: course.checkOutDateTime
        };
      });
      setSelection(initialSelection);
    }
  }, [opened, selectedCourses]);

  const { data, isFetching } = useQuery({
    queryKey: ['lcms-courses-selector', searchKeyword, page, competencyType, jobPositions],
    queryFn: () =>
      courseService.list({
        search: searchKeyword,
        pageNumber: page,
        pageSize: 10,
        // Pass job positions for all competency types
        ...(jobPositions.length > 0 ? { jobPosition: jobPositions.join(',') } : {})
      }),
    placeholderData: (previousData) => previousData,
    enabled: opened
  });

  const courses: CourseOption[] = (data?.data || []).map((item: any) => ({
    msLCMSCourseID: item.msLCMSCourseID || item.courseId || item.id,
    courseName: item.courseName || item.name || '-',
    courseCode: item.courseCode || item.code,
    courseCategory: item.courseCategory || item.category,
    status: item.status,
    competencyCategory: item.competencyCategory,
    scheduleStartDate: item.schedule?.startDate ?? null,
    scheduleEndDate: item.schedule?.endDate ?? null,
    createdAt: item.createdAt,
    createdBy: item.createdBy,
    checkInByName: item.checkInBy?.name,
    checkInDateTime: item.checkInBy?.checkInDateTime,
    checkOutByName: item.checkOutBy?.name,
    checkOutDateTime: item.checkOutBy?.checkOutDateTime
  }));
  const totalPages = data?.totalPages || 1;

  const toggleSelection = (course: CourseOption) => {
    setSelection((prev) => {
      const next = { ...prev };
      if (next[course.msLCMSCourseID]) {
        delete next[course.msLCMSCourseID];
      } else {
        next[course.msLCMSCourseID] = course;
      }
      return next;
    });
  };

  const handleConfirm = () => {
    const selectedList = Object.values(selection).map((item) => ({
      courseId: item.msLCMSCourseID,
      courseName: item.courseName,
      courseCode: item.courseCode,
      courseCategory: item.courseCategory,
      status: item.status,
      competencyCategory: item.competencyCategory,
      scheduleStartDate: item.scheduleStartDate,
      scheduleEndDate: item.scheduleEndDate,
      createdAt: item.createdAt,
      createdBy: item.createdBy,
      checkInByName: item.checkInByName,
      checkInDateTime: item.checkInDateTime,
      checkOutByName: item.checkOutByName,
      checkOutDateTime: item.checkOutDateTime
    }));
    onSubmit(selectedList);
  };

  const selectedCount = Object.keys(selection).length;

  return (
    <Modal opened={opened} onClose={onClose} centered size='80%' radius='lg' shadow='lg'>
      <Stack gap='lg'>
        <Group justify='space-between' align='flex-start'>
          <Stack gap={4}>
            <Text fw={700} size='lg'>
              Select Courses
            </Text>
            <Text size='sm' c='dimmed'>
              Choose one or more courses to include in this competency.
            </Text>
          </Stack>
          <Text fw={600} c='blue'>
            Selected: {selectedCount}
          </Text>
        </Group>

        <TextInput
          placeholder='Search courses by name, code, or category'
          value={searchKeyword}
          onChange={(event) => {
            setSearchKeyword(event.currentTarget.value);
            setPage(1);
          }}
          leftSection={<IconSearch size={16} stroke={1.6} />}
          radius='md'
          size='md'
        />

        {isFetching ? (
          <Flex justify='center' align='center' h={120}>
            <Loader size='sm' />
          </Flex>
        ) : courses.length === 0 ? (
          <Stack align='center' gap='md' py='xl'>
            <Text size='sm' c='dimmed' ta='center'>
              No courses found.
            </Text>
            <Button
              variant='light'
              onClick={() => {
                window.open('/app/lcms/courses/add', '_blank');
              }}
            >
              Add New Course
            </Button>
          </Stack>
        ) : (
          <ScrollArea h={320} offsetScrollbars>
            <Table striped highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: 70 }}>Select</Table.Th>
                  <Table.Th>Course</Table.Th>
                  <Table.Th>Course Code</Table.Th>
                  <Table.Th>Category</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Competency Category</Table.Th>
                  <Table.Th>Created</Table.Th>
                  <Table.Th>Check In</Table.Th>
                  <Table.Th>Check Out</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {courses.map((course) => {
                  const checked = Boolean(selection[course.msLCMSCourseID]);
                  return (
                    <Table.Tr key={course.msLCMSCourseID}>
                      <Table.Td>
                        <Checkbox
                          checked={checked}
                          onChange={() => toggleSelection(course)}
                          aria-label={`Select ${course.courseName}`}
                        />
                      </Table.Td>
                      <Table.Td>
                        <Stack gap={2} align='flex-start'>
                          <Text fw={600} size='md'>
                            {course.courseName}
                          </Text>
                          <Text size='xs' c='dimmed'>
                            {course.msLCMSCourseID}
                          </Text>
                        </Stack>
                      </Table.Td>
                      <Table.Td>{course.courseCode || '-'}</Table.Td>
                      <Table.Td>{course.courseCategory || '-'}</Table.Td>
                      <Table.Td>{course.status || '-'}</Table.Td>
                      <Table.Td>{course.competencyCategory || '-'}</Table.Td>
                      <Table.Td>
                        <Stack gap={2} align='flex-start'>
                          <Text size='sm'>{course.createdBy || '-'}</Text>
                          <Text size='xs' c='dimmed'>
                            {formatDateTime(course.createdAt)}
                          </Text>
                        </Stack>
                      </Table.Td>
                      <Table.Td>
                        <Text size='sm'>
                          {formatCheckInfo(course.checkInByName, course.checkInDateTime)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size='sm'>
                          {formatCheckInfo(course.checkOutByName, course.checkOutDateTime)}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}

        <Group justify='center'>
          <Pagination total={Math.max(totalPages, 1)} value={page} onChange={setPage} />
        </Group>

        <Group justify='flex-end'>
          <Button variant='default' onClick={onClose} disabled={fetchingCourseDetails}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={Object.keys(selection).length === 0}
            loading={fetchingCourseDetails}
          >
            Add Courses
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default AddCoreCompetencyPage;
